import type { ReadonlySignal } from '@preact/signals';
import { useCallback, useId, useMemo } from 'preact/hooks';
import {
  Button,
  MediaModal,
  type ButtonProps
} from '@nespresso-design-system/components';
import FormattedText from '@nespresso/catalog-components-react/containers/FormattedText';
import useTranslations from '@/widgets/hooks/useTranslations';
import styles from './PromotionCardModal.module.scss';
import { trackPlpInteraction } from '@/widgets/services/TrackingService.ts';
import useMediaQuery from '@/widgets/hooks/useMediaQuery';

type PromotionCardModalProps = {
  copy: string;
  images?: {
    desktopImage: string;
    tabletImage: string;
    mobileImage: string;
  };
  imageAlt: string;
  heading: string;
  url: string;
  label: string;
  disclaimer: string;
  openInNewTab: boolean;
  isOpen: ReadonlySignal<boolean>;
  onClose: () => void;
};

export default function PromotionCardModal({
  images,
  imageAlt,
  heading,
  copy,
  url,
  label,
  disclaimer,
  openInNewTab,
  isOpen,
  onClose
}: PromotionCardModalProps) {
  const { t } = useTranslations();
  const closeBtnA11yLabel = t('productList.cards.promotion.closeBtnA11yLabel');
  const { isMobile, isDesktop, isTablet } = useMediaQuery();
  const id = useId();

  const trackCTA = useCallback(() => {
    trackPlpInteraction({
      ctaName: label,
      clickLocation: `plp promo card - ${heading}`,
      actionType: 'promo card modal link click'
    });
  }, [label, heading]);

  const description = useMemo(
    () => (
      <div className={styles.modalContent}>
        {copy && (
          <FormattedText className={styles.modalCopy}>{copy}</FormattedText>
        )}
        {disclaimer && (
          <FormattedText className={styles.modalDisclaimer}>
            {disclaimer}
          </FormattedText>
        )}
        {url && (
          <div class={styles.modalCTAContainer}>
            <Button
              href={url}
              label={label}
              target={openInNewTab ? '_blank' : '_self'}
              rel={openInNewTab ? 'noreferrer' : ''}
              size={'medium' as ButtonProps['size']}
              variant="secondary"
              className={styles.button}
              onClick={trackCTA}
            />
          </div>
        )}
      </div>
    ),
    [
      images?.desktopImage,
      images?.tabletImage,
      images?.desktopImage,
      imageAlt,
      heading,
      copy,
      disclaimer,
      url,
      openInNewTab
    ]
  );

  const { mobileImage, tabletImage, desktopImage } = images ?? {};

  const defaultImg = mobileImage;
  const imageForThisBreakpoint =
    (isMobile && mobileImage) ||
    (isTablet && tabletImage) ||
    (isDesktop && desktopImage) ||
    defaultImg;

  const alt = imageAlt === undefined ? heading : imageAlt;

  return (
    <MediaModal
      closeBtnA11yLabel={closeBtnA11yLabel}
      contentSlot={description}
      heading={heading}
      heroImgAlt={alt}
      heroImgRetinaSrc={images?.desktopImage}
      heroImgSrc={import.meta.env.SSR ? defaultImg : imageForThisBreakpoint}
      id={id}
      isOpen={isOpen.value}
      onClose={onClose}
    />
  );
}
